import usePricing, { type ApiInvoice, type ApiPricing, type Pricing, apiInvoiceToInvoice, apiPricingsToPricings } from './pricing';

export enum ReservationType {
    Single = 'single',
    Recurrent = 'recurrent'
}
export enum PricingMixEnum {
    free = 'free',
    flatFees = 'flatFee',
    basedOnAllocations = 'basedOnAllocations',
    basedOnVehicleMovements = 'basedOnVehicleMovements',
    flatFreeBasedOnAllocations = 'flatFreeBasedOnAllocations',
    flatFreebasedOnVehicleMovements = 'flatFreebasedOnVehicleMovements'
}

export interface Guest {
    name: string;
    uid: string;
}

// config.features (reservation-api). Each *Overridable flag controls whether a
// user-group packet may override the matching feature.
export interface ConfigFeatures {
    enablePersonalFleet?: boolean;
    enablePersonalFleetOverridable?: boolean;
    enableGuest?: boolean;
    enableGuestOverridable?: boolean;
    enableReservation?: boolean; // was isReservable
    enableReservationOverridable?: boolean;
    enableAccessProfile?: boolean;
    enableAccessProfileOverridable?: boolean;
    enableGroupFleet?: boolean;
    enableGroupFleetOverridable?: boolean;
    enableDistantRetrieval?: boolean;
    enableDistantRetrievalOverridable?: boolean;
    enableFlowtingSlots?: boolean;
    useUserGroupAdminAsFleetAdmin?: boolean;
    useUserGroupAdminAsFleetAdminOverridable?: boolean;
}

// config.timing (integers — seconds, except userGroupSlotNumber which is a count).
export interface ConfigTiming {
    instantReservationDefaultDuration?: number; // was maxOccupationTime
    reservationSystemDelta?: number;
    preReservationUserDelta?: number;
    annulationDelta?: number;
    userGroupSlotNumber?: number;
}

// config.notifications (deltas are seconds before the event).
export interface ConfigNotifications {
    enableStartNotification?: boolean;
    startNotificationDelta?: number;
    enableEndNotification?: boolean;
    endNotificationDelta?: number;
}

// config.adminOptions (booleans).
export interface ConfigAdminOptions {
    skipFleetAdminConfig?: boolean;
    skipGroupAdminConfig?: boolean;
    seePlanification?: boolean;
    enableWebAccess?: boolean;
    lockAccessProfileOnProgressReservation?: boolean;
    enableQuickRegistration?: boolean;
}

export interface Config {
    // Nested config sub-components (reservation-api).
    features?: ConfigFeatures;
    timing?: ConfigTiming;
    notifications?: ConfigNotifications;
    adminOptions?: ConfigAdminOptions;
    isPublished?: boolean;
    isBilledForCharging?: boolean;
    // Legacy flat fields — retained for the class.ts / instance.ts
    // reservation-instance path (not restructured here). `maxOccupationTime` and
    // `isReservable` are superseded by config.timing.instantReservationDefaultDuration
    // and config.features.enableReservation respectively.
    maxOccupationTime?: number;
    type?: ReservationType;
    byGroup?: boolean;
    byCar?: boolean;
    isReservable?: boolean;
    guests?: Guest[];
}

// Spec defaults for a freshly created reservation-class config. Booleans default
// false unless noted; a few feature flags default true.
export const defaultReservationConfig: Config = {
    features: {
        enablePersonalFleet: true,
        enablePersonalFleetOverridable: true,
        enableGuest: true,
        enableGuestOverridable: true,
        enableReservation: true,
        enableReservationOverridable: true,
        enableAccessProfile: true,
        enableAccessProfileOverridable: true,
        enableGroupFleet: true,
        enableGroupFleetOverridable: true,
        enableDistantRetrieval: false,
        enableDistantRetrievalOverridable: true,
        enableFlowtingSlots: false,
        useUserGroupAdminAsFleetAdmin: false,
        useUserGroupAdminAsFleetAdminOverridable: false,
    },
    timing: {
        instantReservationDefaultDuration: 36000,
        reservationSystemDelta: 900,
        preReservationUserDelta: 900,
        annulationDelta: 900,
        userGroupSlotNumber: 0,
    },
    notifications: {
        enableStartNotification: false,
        startNotificationDelta: 900,
        enableEndNotification: false,
        endNotificationDelta: 900,
    },
    adminOptions: {
        skipFleetAdminConfig: false,
        skipGroupAdminConfig: false,
        seePlanification: false,
        enableWebAccess: false,
        lockAccessProfileOnProgressReservation: true,
        enableQuickRegistration: false,
    },
    isPublished: false,
    isBilledForCharging: false,
};

export interface BillToClient {
    biller: string;
    currency: string;
    pricingMix: PricingMixEnum;
    flatFees: Pricing[];
    powerSupplyFees: Pricing[];
}
export interface BillToClientProps {
    biller?: string;
    currency?: string;
    pricingMix?: PricingMixEnum;
    flatFees?: Pricing[];
    powerSupplyFees?: Pricing[];
}

export interface ApiBillToClient {
    biller: string;
    currency: string;
    pricingMix: PricingMixEnum;
    flatFees: ApiPricing[];
    powerSupplyFees: ApiPricing[];
}


export interface Invoice {
    userId: string,
    calculatedPrice: number,
    date: Date,
    pricing: Pricing,
    reservationClass: ReservationClass
}
export interface EChargingPrices {
    id?: number | string;
    pricing?: Invoice;
    isVIP: boolean;
    biller: string;
}


export interface TimeslotsPrices {
    biller: string;
    flatDays: Pricing | Pricing[];
    hours: Pricing | Pricing[];
}

export interface BillToEndUser {
    biller: string;
    currency: string;
    eChargingPrices: EChargingPrices[];
    timeslotsPrices?: TimeslotsPrices;
}
interface ApiECharging {
    id?: string | number;
    documentId?: string;
    biller: string;
    isVIP: boolean;
    pricing:  ApiInvoice ;
}

interface ApiTimeslots {
    id?: string | number;
    documentId?: string;
    biller: string;
    flatDays:  ApiPricing[] ;
    hours: ApiPricing[] ;
}
interface ApiBillToEndUser {
    biller: string;
    currency: string;
    eChargingPrices?: ApiECharging[];
    timeslotsPrices?: ApiTimeslots;
}
export interface ReservationClassProps {
    clientId?: string;
    id?: string;
    userId?: string;
    parkingId?: string;
    fleetId?: string;
    instancePricing?: Pricing;
    startTime?: Date;
    endTime?: Date;
    recurrentPricing?: Pricing;
    config?: Config;
    maxOccupationTime?: number;
    type?: ReservationType;
    byGroup?: boolean;
    byCar?: boolean;
    isReservable?: boolean;
    guests?: Guest[];
    slotNumber?: number;
    billToClient?: ApiBillToClient | BillToClient;
    billToEndUser?: BillToEndUser;
    timeslotsPrices?: TimeslotsPrices
}

interface ApiReservationClass {
    id?: string;
    documentId?: string;
    clientId?: string;
    config?: Config;
    endTime?: string;
    byGroup?: boolean;
    fleetId?: string;
    byCar?: boolean;
    guests?: Guest[];
    parkingId?: string;
    userId?: string;
    maxOccupationTime?: number;
    slotNumber?: number;
    startTime?: string;
    isReservable?: boolean;
    recurrentPricing?: boolean;
    billToClient?: ApiBillToClient | BillToClient;
    billToEndUser?: ApiBillToEndUser;
}

export type { ApiReservationClass };

interface ReservationClass {
    id: string;
    documentId?: string;
    clientId?: string;
    userId?: string;
    parkingId?: string;
    fleetId?: string;
    instancePricing?: Pricing;
    startTime?: Date;
    endTime?: Date;
    recurrentPricing?: Pricing | boolean;
    config?: Config;
    maxOccupationTime?: number;
    type?: ReservationType;
    byGroup?: boolean;
    byCar?: boolean;
    isReservable?: boolean;
    guests?: Guest[];
    slotNumber?: number;
    billToClient?: BillToClient;
    billToEndUser?: BillToEndUser;
    timeslotsPrices?: TimeslotsPrices;
}

export type { ReservationClass };

export interface BillToClientProps {

}
export const convertToPricing = (pricingToConvert: Pricing): Pricing => pricingToConvert;

export const convertToInvoice = (invoiceToConvert: Invoice): Invoice => invoiceToConvert;
export const convertToPricings = (pricingsToConvert: Pricing[]): Pricing[] => {
    return pricingsToConvert.map(pricing => convertToPricing(pricing));
};

const apiBillToClientToBillClient = (apiBillToClient: ApiBillToClient): BillToClient => {
    const { apiPricingsToPricings } = usePricing;
    return {
        biller: apiBillToClient.biller,
        currency: apiBillToClient.currency,
        flatFees: apiBillToClient.flatFees ? apiPricingsToPricings(apiBillToClient.flatFees) : [],
        powerSupplyFees: apiBillToClient.powerSupplyFees ? apiPricingsToPricings(apiBillToClient.powerSupplyFees) : [],
        pricingMix: apiBillToClient.pricingMix,
    };
};

const apiEChargingToECharging = (apiECharging: ApiECharging): EChargingPrices => {
    return {
        biller: apiECharging.biller,
        id: apiECharging.id,
        isVIP: apiECharging.isVIP,
        pricing: apiECharging.pricing ? apiInvoiceToInvoice(apiECharging.pricing) : undefined
    };
};

const apiEChargingsToEChargings = (apiEChargings: ApiECharging[]): EChargingPrices[] => {
    return apiEChargings.map(item => apiEChargingToECharging(item));
};

const apiTimeslotsToTimeslots = (apiTimeslots: ApiTimeslots): TimeslotsPrices => {
    return {
        biller: apiTimeslots.biller,
        flatDays: apiTimeslots.flatDays ? apiPricingsToPricings(apiTimeslots.flatDays) : [],
        hours: apiTimeslots.hours ? apiPricingsToPricings(apiTimeslots.hours) : [],
    };
};


const apiBillToEndUserToBillToEndUser = (apiBillToEndUser: ApiBillToEndUser): BillToEndUser => {
    return {
        biller: apiBillToEndUser.biller,
        currency: apiBillToEndUser.currency,
        eChargingPrices: apiBillToEndUser.eChargingPrices ? apiEChargingsToEChargings(apiBillToEndUser.eChargingPrices) : [],
        timeslotsPrices: apiBillToEndUser.timeslotsPrices ? apiTimeslotsToTimeslots(apiBillToEndUser.timeslotsPrices) : undefined,
    };
};


const apiResaClassToResaClass = (apiResaClass: ApiReservationClass): ReservationClass => {
    const finalId = apiResaClass.id || apiResaClass.documentId;
    
    if (!finalId) {
        throw new Error('Missing id or documentId in ApiReservationClass');
    }

    const {
        clientId,
        config,
        endTime,
        byGroup,
        fleetId,
        byCar,
        guests,
        parkingId,
        userId,
        maxOccupationTime,
        slotNumber,
        startTime,
        isReservable,
        billToClient,
        billToEndUser,
        recurrentPricing
    } = apiResaClass;

    const parsedEndTime = endTime ? new Date(endTime) : undefined;
    const parsedStartTime = startTime ? new Date(startTime) : undefined;

    return {
        billToClient: billToClient ? apiBillToClientToBillClient(billToClient as ApiBillToClient) : undefined,
        byCar,
        byGroup,
        clientId,
        config,
        documentId: apiResaClass.documentId,
        endTime: parsedEndTime,
        fleetId,
        guests,
        id: finalId,
        isReservable,
        maxOccupationTime,
        parkingId,
        recurrentPricing,
        slotNumber,
        startTime: parsedStartTime,
        userId,
    };
};


const apiResaClassesToResaClasses = (apiResaClasses: ApiReservationClass[]): ReservationClass[] => {
    return apiResaClasses.map((apiResaClass) => apiResaClassToResaClass(apiResaClass));
};


const resaClassToApiResaClass = (resaClass: ReservationClass): ApiReservationClass => {
    const serializedEndTime = resaClass.endTime instanceof Date ? resaClass.endTime.toISOString() : resaClass.endTime;
    const serializedStartTime = resaClass.startTime instanceof Date ? resaClass.startTime.toISOString() : resaClass.startTime;
    
    return {
        id: resaClass.id ?? '',
        byCar: resaClass.byCar,
        byGroup: resaClass.byGroup,
        config: resaClass.config,
        endTime: serializedEndTime,
        fleetId: resaClass.fleetId,
        guests: resaClass.guests,
        instancePricing: resaClass.instancePricing,
        isReservable: resaClass.isReservable,
        maxOccupationTime: resaClass.maxOccupationTime,
        parkingId: resaClass.parkingId,
        recurrentPricing: resaClass.recurrentPricing,
        slotNumber: resaClass.slotNumber,
        startTime: serializedStartTime,
        type: resaClass.type,
        userId: resaClass.userId,
    } as ApiReservationClass;
};
const resaClassToPostResaClass = (resaClass: ReservationClass): ReservationClassProps => {
    return {
        billToClient: resaClass.billToClient,
        billToEndUser: resaClass.billToEndUser,
        byCar: resaClass.byCar,
        byGroup: resaClass.byGroup,
        config: resaClass.config,
        endTime: resaClass.endTime,
        fleetId: resaClass.fleetId,
        guests: resaClass.guests,
        instancePricing: resaClass.instancePricing,
        isReservable: resaClass.isReservable,
        maxOccupationTime: resaClass.maxOccupationTime,
        parkingId: resaClass.parkingId,
        recurrentPricing: typeof resaClass.recurrentPricing === 'boolean' ? undefined : resaClass.recurrentPricing,
        slotNumber: resaClass.slotNumber,
        startTime: resaClass.startTime,
        type: resaClass.type,
        userId: resaClass.userId
    };
};

const useResaClass = {
    apiBillToClientToBillClient,
    apiEChargingToECharging,
    apiResaClassToResaClass,
    apiResaClassesToResaClasses,
    resaClassToApiResaClass,
    resaClassToPostResaClass
};

export default useResaClass;
